import { ADS_SDK_UNHANDLED_ERROR } from 'App/Services/Ads/constants';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

const AD_SDK_HOST_PATTERNS = ['pubpowerplatform.io', 'pubpowerplatform'];
const AD_SDK_MESSAGE_PATTERNS = [/bidBackHandler/i, /pubpowerplatform/i];

function includesAny(haystack, patterns) {
    return patterns.some((p) => haystack.includes(p));
}

function matchesAnyRegex(haystack, patterns) {
    return patterns.some((re) => re.test(haystack));
}

function isAdSdkErrorEvent(event) {
    const filename = String(event?.filename || '');
    const message = String(event?.message || '');
    const stack = String(event?.error?.stack || '');

    // Prefer URL / stack evidence
    if (includesAny(filename, AD_SDK_HOST_PATTERNS)) return true;
    if (includesAny(stack, AD_SDK_HOST_PATTERNS)) return true;

    // Fallback on a message (narrow)
    return matchesAnyRegex(message, AD_SDK_MESSAGE_PATTERNS);
}

function isAdSdkRejection(event) {
    const reason = event?.reason;
    const message = String(reason?.message || reason || '');
    const stack = String(reason?.stack || '');

    if (includesAny(stack, AD_SDK_HOST_PATTERNS)) return true;
    if (includesAny(message, AD_SDK_HOST_PATTERNS)) return true;

    return matchesAnyRegex(message, AD_SDK_MESSAGE_PATTERNS);
}

/**
 * Prevent ad SDK errors from reaching Unity's error handler (Unity modal),
 * while NOT blocking Sentry/other error logic.
 */
export function installAdErrorHandler() {
    if (typeof window === 'undefined') return () => {};

    const onErrorCapture = (event) => {
        // Only suppress ad SDK errors
        if (!isAdSdkErrorEvent(event)) return;

        const error =
            event.error instanceof Error
                ? event.error
                : new Error(
                      event.message || 'No message on window error event'
                  );

        try {
            captureCustomException(error, ADS_SDK_UNHANDLED_ERROR, {
                context: 'ad_sdk_window_error',
                filename: event.filename || 'unknown',
            });
        } catch {
            console.warn('ad_sdk_window_error');
        }

        // Critical: stop propagation to bubble listeners (Unity),
        // but do NOT stop other window listeners on the same phase.
        event.preventDefault?.();
        event.stopPropagation?.();
    };

    const onUnhandledRejectionCapture = (event) => {
        if (!isAdSdkRejection(event)) return;

        const reason = event.reason;
        const error =
            reason instanceof Error
                ? reason
                : new Error(String(reason || 'No reason on promise rejection'));

        try {
            captureCustomException(error, ADS_SDK_UNHANDLED_ERROR, {
                context: 'ad_sdk_unhandledrejection',
            });
        } catch {
            console.warn('ad_sdk_unhandledrejection');
        }

        // Prevent bubbling to handlers that would surface it as a fatal Unity error
        event.preventDefault?.();
        event.stopPropagation?.();
    };

    // Capture phase so we can stop it before Unity bubble handlers see it
    window.addEventListener('error', onErrorCapture, { capture: true });
    window.addEventListener('unhandledrejection', onUnhandledRejectionCapture, {
        capture: true,
    });

    return () => {
        window.removeEventListener('error', onErrorCapture, { capture: true });
        window.removeEventListener(
            'unhandledrejection',
            onUnhandledRejectionCapture,
            { capture: true }
        );
    };
}
